import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Briefcase, Users, FileText, DollarSign, 
  TrendingUp, Clock, AlertCircle
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Active Jobs', 
      value: stats?.active_jobs || 0, 
      total: stats?.total_jobs || 0,
      icon: Briefcase, 
      color: 'text-blue-400',
      bg: 'bg-blue-400/10'
    },
    { 
      title: 'Contractors', 
      value: stats?.total_contractors || 0, 
      icon: Users, 
      color: 'text-green-400',
      bg: 'bg-green-400/10'
    },
    { 
      title: 'Pending Quotes', 
      value: stats?.pending_quotes || 0, 
      icon: FileText, 
      color: 'text-yellow-400',
      bg: 'bg-yellow-400/10'
    },
    { 
      title: 'Pending Invoices', 
      value: stats?.pending_invoices || 0, 
      icon: AlertCircle, 
      color: 'text-orange-400',
      bg: 'bg-orange-400/10'
    },
    { 
      title: 'Total Revenue', 
      value: `$${(stats?.total_revenue || 0).toLocaleString()}`, 
      icon: DollarSign, 
      color: 'text-primary',
      bg: 'bg-primary/10'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Welcome back, {user?.name?.split(' ')[0]}</h1>
          <p className="text-muted-foreground mt-1">Here's what's happening with your projects</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          {new Date().toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="bg-card border-border rounded-sm" data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                    <p className="text-2xl font-bold mt-1">{stat.value}</p>
                    {stat.total && (
                      <p className="text-xs text-muted-foreground">of {stat.total} total</p>
                    )}
                  </div>
                  <div className={`p-3 ${stat.bg} rounded-sm`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Activity */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="border-b border-border">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {stats?.recent_activity?.length > 0 ? (
            <div className="divide-y divide-border">
              {stats.recent_activity.map((activity, index) => (
                <div key={activity.id || index} className="flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    <div>
                      <p className="text-sm font-medium">
                        <span className="text-muted-foreground">{activity.user_name}</span>
                        {' '}{activity.action}{' '}
                        <span className="text-primary">{activity.entity_type}</span>
                      </p>
                      {activity.details && (
                        <p className="text-xs text-muted-foreground mt-0.5">{activity.details}</p>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              No recent activity
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
